import { Form, Formik, FormikProps, validateYupSchema, yupToFormErrors } from "formik";
import React, { useContext, useEffect, useState } from "react";
import useErrorLogger from "../../../common/hooks/useErrorLogger";
import { IQuoteSettings } from "../../interfaces/form/IQuoteSettings";
import cx from "classnames";
import useSettingsValidation from "../../hooks/useSettingsValidation";
import { QuoteSettingContainerType } from "../../../common/interfaces/settings/QuoteSettingContainerType";
import useSettingsBuilder from "../../hooks/useSettingsBuilder";
import { IClient } from "../../../common/interfaces/IClient";
import ProductType from "../../../common/interfaces/enums/ProductType";
import useIsMounted from "../../../common/hooks/useIsMounted";
import { QuoteGroupContext } from "../../providers/RootContextProvider";

interface IBaseProductFormProps {
    className?: string;
    clients: IClient[];
    // This is the products we are validating.
    compareProducts?: ProductType[] | ProductType;
    validationProducts?: ProductType[];
    // This is all the settings objects we currently have. Some insurance types depend on each other. So taking multiple settings objects is advantageous.
    settings: QuoteSettingContainerType[] | QuoteSettingContainerType;
    onSubmit: (
        settings: QuoteSettingContainerType[],
        clients: IClient[]
    ) => Promise<void>;
    children: (formik: FormikProps<IQuoteSettings>) => React.ReactNode;
}

const BaseProductForm: React.FC<IBaseProductFormProps> = (props) => {
    const { className, clients, onSubmit, validationProducts, children } = props;

    const ErrorLogger = useErrorLogger();
    const { createSchema } = useSettingsValidation();
    const { buildQuoteSettings, build, buildClient } = useSettingsBuilder();
    const isMounted = useIsMounted();
    const { selectors } = useContext(QuoteGroupContext);

    const settings = Array.isArray(props.settings) ? props.settings : [props.settings];
    // We either compare just the given products, or all of the products we have settings for
    const compareProducts = props.compareProducts
        ? Array.isArray(props.compareProducts)
            ? props.compareProducts
            : [props.compareProducts]
        : settings.map((s) => s.productType);

    const validationSchema = createSchema(validationProducts || compareProducts);

    const buildState = (): IQuoteSettings => {
        return buildQuoteSettings(compareProducts, settings, clients);
    };

    const [initialValues, setInitialValues] = useState<IQuoteSettings>(buildState());

    const isLoadingResults = selectors.isLoadingQuoteResult();

    // Whenever new results have finished loading we need to create the new initial values and re-initialize the form
    useEffect(() => {
        if (!isLoadingResults) setInitialValues(buildState());
    }, [isLoadingResults, clients]);

    const firstChildPerson = initialValues.clients.find((c) => c.isChild);

    return (
        <Formik
            initialValues={initialValues}
            isInitialValid={validationSchema.isValidSync(initialValues)}
            onSubmit={async (values, formikHelpers) => {
                // We clone the children settings across all the child clients.
                if (firstChildPerson) {
                    const baseChild = values.clients.find(
                        (s) => s.personId === firstChildPerson.personId
                    );

                    values.clients = values.clients.map((s) => {
                        if (s.isChild) {
                            return {
                                ...baseChild,
                                personId: s.personId,
                                age: s.age,
                                gender: s.gender
                            };
                        } else {
                            return s;
                        }
                    });
                }

                const newSettings = compareProducts.map((p) => build(values, p));

                await onSubmit(newSettings, buildClient(values))
                    .catch((err) =>
                        ErrorLogger.Log(err, {
                            source: `Something went wrong in the base form`
                        })
                    )
                    .finally(() => {
                        isMounted.current && formikHelpers.setSubmitting(false);
                    });
            }}
            validate={async (values) => {
                try {
                    // We do this approach, as formik doesn't provide the values as a context (4th arg)
                    // This makes it easier to validate against values at the root of the object (Mortgage & Life)
                    await validateYupSchema(values, validationSchema, true, values);
                } catch (err) {
                    return yupToFormErrors(err);
                }
                return {};
            }}>
            {(formik) => (
                <Form
                    className={cx({
                        [className]: !!className
                    })}>
                    {children(formik)}
                </Form>
            )}
        </Formik>
    );
};

export default BaseProductForm;
